import React, { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import Handler from "../../../Helpers/Handler";
import { formatDateTime } from "../../../Helpers/Utils";
import { serviceStatusVariant } from "./vehicleFleetConstants";
import ServiceCreateModal from "../Services/ServiceCreateModal";

// Recent service history for one vehicle (vehicleServiceList, most recent
// first) plus a read-only "services due" strip from pmsDue - PMS itself is
// never edited directly here, it's populated automatically whenever a
// service's serviceEnd part names match a maintenance service definition
// (see ServiceController.js's serviceEnd). Full history/workflow lives on
// each service's own detail page.
const VehicleServicePanel = ({ vehicleType, vehicleId }) => {
    const navigate = useNavigate();
    const [services, setServices] = useState([]);
    const [due, setDue] = useState([]);
    const [loading, setLoading] = useState(true);
    const [showCreateModal, setShowCreateModal] = useState(false);

    const load = useCallback(async () => {
        setLoading(true);
        const [historyResponse, dueResponse] = await Promise.all([
            Handler({ method: "post", url: `/admin/service/${vehicleType}/${vehicleId}/list/0`, params: { count: 5 } }),
            Handler({ method: "GET", url: `/admin/pms/${vehicleType}/${vehicleId}/due` }),
        ]);
        setServices(historyResponse?.success ? historyResponse?.data?.data?.list || [] : []);
        setDue(dueResponse?.success ? dueResponse?.data?.data || [] : []);
        setLoading(false);
    }, [vehicleType, vehicleId]);

    useEffect(() => { load(); }, [load]);

    if (loading) return <p className="text-muted mb-0">Loading service history...</p>;

    return (
        <div>
            {due.length ? (
                <div className="mb-3">
                    <div className="small fw-semibold mb-2">Services due</div>
                    <div className="d-flex flex-wrap gap-2">
                        {due.map((d) => (
                            <span key={d.serviceId} className="badge rounded-pill text-bg-warning-subtle border border-warning text-dark" title={d.reason}>
                                {d.serviceName}
                            </span>
                        ))}
                    </div>
                </div>
            ) : null}

            {services.length ? (
                <div className="d-flex flex-column gap-2 mb-3">
                    {services.map((s) => (
                        <button
                            key={s.serviceId}
                            type="button"
                            className="btn btn-outline-secondary d-flex align-items-center justify-content-between text-start"
                            onClick={() => navigate(`/vehicle-fleet/services/${s.serviceId}`)}
                        >
                            <span>{s.serviceName} <span className="text-muted small">- {s.serviceType}{s.servicedEndAt ? ` - ${formatDateTime(s.servicedEndAt)}` : ""}</span></span>
                            <span className={`badge rounded-pill ${serviceStatusVariant(s.serviceStatus)}`}>{s.serviceStatus}</span>
                        </button>
                    ))}
                </div>
            ) : (
                <p className="text-muted small">No service records yet.</p>
            )}

            <button type="button" className="btn btn-sm btn-outline-primary" onClick={() => setShowCreateModal(true)}>+ New service</button>

            {showCreateModal ? (
                <ServiceCreateModal
                    vehicleType={vehicleType}
                    vehicleId={vehicleId}
                    onClose={() => setShowCreateModal(false)}
                    onSaved={() => { setShowCreateModal(false); load(); }}
                />
            ) : null}
        </div>
    );
};

export default VehicleServicePanel;
